import { MAP_TEXT_LABEL_FONT_OPTIONS } from "@/lib/textLabelFonts";
import { TEXT_LABEL_SIZE_OPTIONS } from "@/lib/textLabelSizes";
import { getBroadcastMapChange } from "@/state/realtimeBroadcast";
import { useEditorStore } from "@/state/useEditorStore";
import { useMapStore } from "@/state/useMapStore";
import { TEXT_LABEL_COLOR_PRESETS, useTextLabelStore } from "@/state/useTextLabelStore";
import { getMapChromeTheme } from "@/components/mapChromeTheme";
import type { MapTextLabel } from "@west-marches/shared";

export function TextStyleToolbar() {
  const theme = getMapChromeTheme();
  const activeTool = useEditorStore((s) => s.activeTool);
  const editingLabelId = useTextLabelStore((s) => s.editingLabelId);
  const selectedLabelId = useTextLabelStore((s) => s.selectedLabelId);
  const pendingStyle = useTextLabelStore((s) => s.pendingStyle);
  const setPendingStyle = useTextLabelStore((s) => s.setPendingStyle);
  const mapState = useMapStore((s) => s.mapState);
  const applyMapChanges = useMapStore((s) => s.applyMapChanges);
  const pushHistory = useEditorStore((s) => s.pushHistory);

  const targetId = editingLabelId !== "new" ? editingLabelId : null;
  const activeId = targetId ?? selectedLabelId;
  const activeLabel =
    activeId && mapState ? mapState.textLabels[activeId] ?? null : null;

  const currentStyle = activeLabel
    ? {
        fontSize: activeLabel.fontSize,
        fontFamily: activeLabel.fontFamily,
        color: activeLabel.color,
        bold: activeLabel.bold,
        italic: activeLabel.italic,
        banner: activeLabel.banner,
      }
    : pendingStyle;
  const mapFont = MAP_TEXT_LABEL_FONT_OPTIONS[0]!;

  if (activeTool !== "text" && !editingLabelId && !selectedLabelId) return null;

  function applyStyle(partial: Partial<MapTextLabel>) {
    const normalizedPartial: Partial<MapTextLabel> = {
      ...partial,
      fontFamily: mapFont.id,
    };
    if (activeLabel) {
      const updated = { ...activeLabel, ...normalizedPartial };
      const before = { textLabels: { [activeLabel.id]: { ...activeLabel } } };
      const after = { textLabels: { [updated.id]: updated } };
      applyMapChanges(after);
      pushHistory({ before, after });
      getBroadcastMapChange()(after);
    }
    setPendingStyle(normalizedPartial);
  }

  return (
    <div
      data-text-style-toolbar
      className={`fixed bottom-28 left-1/2 z-20 flex w-max max-w-[min(92vw,720px)] -translate-x-1/2 flex-col gap-2 px-2.5 py-2 ${theme.floatingPanel}`}
    >
      <div className={`text-[10px] tracking-[0.2em] ${theme.textSubtle}`}>Text size</div>

      <div className="flex flex-wrap items-center gap-1.5">
        {TEXT_LABEL_SIZE_OPTIONS.slice().reverse().map((size) => {
          const selected = currentStyle.fontSize === size.fontSize;
          return (
            <button
              key={size.id}
              type="button"
              onClick={() => applyStyle({ fontSize: size.fontSize, fontFamily: mapFont.id })}
              className={`flex size-14 shrink-0 items-center justify-center rounded-lg border text-xs font-semibold uppercase tracking-[0.08em] transition-colors sm:text-sm sm:tracking-widest ${
                selected
                  ? theme.sizeOptionSelected
                  : theme.sizeOptionIdle
              }`}
            >
              {size.label}
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1">
          {TEXT_LABEL_COLOR_PRESETS.map((color) => (
            <button
              key={color}
              type="button"
              onClick={() => applyStyle({ color })}
              className={`h-5 w-5 rounded-full border-2 transition-transform ${
                currentStyle.color === color
                  ? "scale-110 border-white"
                  : "border-white/30 hover:border-white/60"
              }`}
              style={{ backgroundColor: color }}
            />
          ))}
        </div>

        <div className={`h-5 w-px ${theme.divider}`} />

        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => applyStyle({ bold: !currentStyle.bold })}
            className={`flex h-7 w-7 items-center justify-center rounded text-sm font-bold transition-colors ${
              currentStyle.bold
                ? theme.iconToggleActive
                : theme.iconToggleIdle
            }`}
          >
            B
          </button>
          <button
            type="button"
            onClick={() => applyStyle({ italic: !currentStyle.italic })}
            className={`flex h-7 w-7 items-center justify-center rounded text-sm italic transition-colors ${
              currentStyle.italic
                ? theme.iconToggleActive
                : theme.iconToggleIdle
            }`}
          >
            I
          </button>
        </div>

        <div className={`h-5 w-px ${theme.divider}`} />

        <button
          type="button"
          onClick={() => applyStyle({ banner: !currentStyle.banner })}
          aria-pressed={currentStyle.banner}
          title={currentStyle.banner ? "Hide banner" : "Show banner"}
          data-testid="text-banner-toggle"
          className={`flex h-7 items-center gap-1 rounded px-2 text-[10px] font-semibold uppercase tracking-[0.16em] transition-colors ${
            currentStyle.banner ? theme.iconToggleActive : theme.iconToggleIdle
          }`}
        >
          Banner
        </button>
      </div>
    </div>
  );
}
